import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import moment from "moment";
import { notification } from "antd";

import SmartRtpTablePage from "./components/SmartRtpTablePage";
import SmartRtpCards from "./components/SmartRtpCards";
import { TZ } from "../../../config/constants";
import { formatSmartRtpNumber } from "../../../utils/bigMath";
import { WINNER_SEARCH_NUMERIC_FIELDS, WINNER_SEARCH_OPTIONS } from "./config";
import {
  getWinnersDailySummaries,
  getWinnersDaySummary,
} from "../../../controllers/V1/smartRtp/smartRtpController";

function formatDateOnly(value) {
  if (!value) return undefined;
  return moment(value).format("YYYY-MM-DD");
}

function formatDisplayDate(value) {
  if (!value) return "-";
  return moment(value, "YYYY-MM-DD").format("DD-MM-YYYY");
}

export default function SmartRtpWinnersPage() {
  const [todaySummary, setTodaySummary] = useState({});
  const [loadingSummary, setLoadingSummary] = useState(false);

  const loadTodaySummary = async () => {
    setLoadingSummary(true);
    const res = await getWinnersDaySummary({});
    if (res.status === true) {
      setTodaySummary(res.result || {});
    } else {
      notification.error({
        message: "Could not load winners summary",
        description: res.message || "Please restart the API server if winners routes were just added.",
      });
    }
    setLoadingSummary(false);
  };

  useEffect(() => {
    loadTodaySummary();
  }, []);

  const cards = [
    {
      key: "winners",
      label: "Total Winners (Today)",
      helper: "Tickets marked as winner on the order",
      value: todaySummary.total_winners || 0,
    },
    {
      key: "winning",
      label: "Total Winning Amount (Today)",
      value: todaySummary.total_winning_amount || 0,
    },
    {
      key: "redeemed",
      label: "Total Redeemed Amount (Today)",
      value: todaySummary.total_redeemed_amount || 0,
    },
    {
      key: "unpaid",
      label: "Total Amount Unpaid (Today)",
      value: todaySummary.total_unpaid_amount || 0,
    },
  ];

  return (
    <SmartRtpTablePage
      title="Smart Winners"
      activeKey="winners"
      fetchData={getWinnersDailySummaries}
      showFilter
      showExport
      searchFields={WINNER_SEARCH_OPTIONS.map((option) => option.value)}
      searchOptions={WINNER_SEARCH_OPTIONS}
      searchNumericFields={WINNER_SEARCH_NUMERIC_FIELDS}
      statusTabs={[{ label: "Daily Summary", value: "" }]}
      exportFileName="Smart Winners Daily Summary"
      exportConfig={{ hideSearch: false }}
      buildOptions={({ skip, limit, filter }) => ({
        skip,
        limit,
        from_date: filter.from_date ? formatDateOnly(filter.from_date) : undefined,
        to_date: filter.to_date ? formatDateOnly(filter.to_date) : undefined,
        days: !filter.from_date && !filter.to_date ? 30 : undefined,
      })}
      extraTop={
        <>
          <div className="small text-muted mb-3">
            Daily cycle uses {TZ} timezone (00:00:00 to 23:59:59). Winners are loaded from
            existing winning tickets on each order — no separate winners table.
          </div>
          <SmartRtpCards cards={cards} />
          {loadingSummary ? (
            <div className="small text-muted mb-3">Loading today summary...</div>
          ) : null}
        </>
      }
      columns={[
        {
          key: "filter_date",
          label: "Date",
          showInExcel: true,
          showInOrderList: true,
          render: (row) => formatDisplayDate(row?.filter_date),
          exportValue: (row) => formatDisplayDate(row?.filter_date),
        },
        {
          key: "total_winners",
          label: "Winning Tickets",
          showInExcel: true,
          showInOrderList: true,
        },
        {
          key: "total_winning_amount",
          label: "Total Winning Amount",
          showInExcel: true,
          showInOrderList: true,
          render: (row) => formatSmartRtpNumber(row?.total_winning_amount || 0, 3),
          exportValue: (row) => row?.total_winning_amount || 0,
        },
        {
          key: "total_redeemed_amount",
          label: "Total Redeemed Amount",
          showInExcel: true,
          showInOrderList: true,
          render: (row) => formatSmartRtpNumber(row?.total_redeemed_amount || 0, 3),
          exportValue: (row) => row?.total_redeemed_amount || 0,
        },
        {
          key: "total_unpaid_amount",
          label: "Total Amount Unpaid",
          showInExcel: true,
          showInOrderList: true,
          render: (row) => formatSmartRtpNumber(row?.total_unpaid_amount || 0, 3),
          exportValue: (row) => row?.total_unpaid_amount || 0,
        },
        {
          key: "actions",
          label: "Action",
          showInExcel: false,
          showInOrderList: true,
          render: (row) => (
            <Link to={`/smart-rtp/winners/details?date=${row.filter_date}`}>
              View Winners
            </Link>
          ),
        },
      ]}
    />
  );
}
